import { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  CareEnvironment,
  CarePhase,
  careEnvironmentsApplicableToPhase,
  carePhaseDefList,
} from '@shared';
import { selectClient } from '@client/model';

export function useCareEnvironmentSelections(
  updatePreviewClient: (updatedClient: Client) => void,
  previewClient: Client,
) {
  const client = useSelector(selectClient);

  const previewClientRef = useRef(previewClient);
  previewClientRef.current = previewClient;

  const initialSelections = () => {
    const selections: Partial<Record<CarePhase, CareEnvironment | null>> = {};
    carePhaseDefList.forEach(carePhaseDef => {
      selections[carePhaseDef.value] = client.careEnvironmentSelections?.[carePhaseDef.value] ?? null;
    });
    return selections;
  };

  const [selections, setSelections] =
    useState<Partial<Record<CarePhase, CareEnvironment | null>>>(initialSelections);

  const selectEnvironment = useCallback(
    (phase: CarePhase, env: CareEnvironment | null) => {
      setSelections(prev => {
        const current = prev[phase];
        const newValue = current === env ? null : env;
        const updated = { ...prev, [phase]: newValue };

        const updatedClient = {
          ...previewClientRef.current,
          careEnvironmentSelections: updated,
        };
        updatePreviewClient(updatedClient);

        return updated;
      });
    },
    [updatePreviewClient],
  );

  const getResetSelectionPatches = useCallback(() => {
    const recommended = client.recommendedCareEnvironments;
    const resetValues: Partial<Record<CarePhase, CareEnvironment | null>> = {};
    carePhaseDefList.forEach(carePhaseDef => {
      resetValues[carePhaseDef.value] = recommended?.[carePhaseDef.value] ?? null;
    });
    setSelections(resetValues);
    return { careEnvironmentSelections: resetValues };
  }, [client.recommendedCareEnvironments]);

  const resetSelections = useCallback(() => {
    const patches = getResetSelectionPatches();
    updatePreviewClient({ ...previewClientRef.current, ...patches });
  }, [getResetSelectionPatches, updatePreviewClient]);

  const isAllDefaultSelections = useCallback(() => {
    const recommended = client.recommendedCareEnvironments;
    return carePhaseDefList.every(carePhaseDef => {
      const selection = selections[carePhaseDef.value];
      const recommendedEnv = recommended?.[carePhaseDef.value] ?? null;
      return selection == null || selection === recommendedEnv;
    });
  }, [selections, client.recommendedCareEnvironments]);

  return {
    selections,
    selectEnvironment,
    resetSelections,
    getResetSelectionPatches,
    isAllDefaultSelections,
    applicableEnvironments: careEnvironmentsApplicableToPhase,
  };
}
